import { useState, useEffect, useCallback } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, AlertTriangle, Image as ImageIcon, Info } from "lucide-react";

interface MediaValidationResult {
  isValid: boolean;
  platform: string;
  warnings: string[];
  errors: string[];
  dimensions?: { width: number; height: number };
  aspectRatio?: string;
  fileSize?: number;
}

interface MediaValidationProps {
  file?: File;
  url?: string;
  platforms: string[];
  onValidationComplete?: (results: Record<string, MediaValidationResult>) => void;
}

const PLATFORM_REQUIREMENTS = {
  instagram: {
    minWidth: 320,
    maxWidth: 1440,
    minHeight: 320,
    maxHeight: 1800,
    aspectRatios: [
      { name: 'Square', ratio: 1, tolerance: 0.01 },
      { name: 'Portrait', ratio: 4 / 5, tolerance: 0.02 },
      { name: 'Landscape', ratio: 1.91, tolerance: 0.02 },
    ],
    maxFileSizeMB: 8,
    formats: ['image/jpeg', 'image/png', 'image/webp'],
  },
  facebook: {
    minWidth: 600,
    maxWidth: 2048,
    minHeight: 315,
    maxHeight: 2048,
    aspectRatios: [
      { name: 'Any', ratio: 0, tolerance: 1 },
    ],
    maxFileSizeMB: 10,
    formats: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  },
};

export function MediaValidation({ file, url, platforms, onValidationComplete }: MediaValidationProps) {
  const [results, setResults] = useState<Record<string, MediaValidationResult>>({});
  const [isValidating, setIsValidating] = useState(false);

  const validateImage = useCallback(async (imageElement: HTMLImageElement, fileSize?: number, fileType?: string) => {
    const newResults: Record<string, MediaValidationResult> = {};

    for (const platform of platforms) {
      const requirements = PLATFORM_REQUIREMENTS[platform as keyof typeof PLATFORM_REQUIREMENTS];
      if (!requirements) continue;

      const result: MediaValidationResult = {
        isValid: true,
        platform,
        warnings: [],
        errors: [],
        dimensions: { width: imageElement.naturalWidth, height: imageElement.naturalHeight },
        fileSize,
      };

      const { naturalWidth: width, naturalHeight: height } = imageElement;
      const aspectRatio = width / height;

      if (width < requirements.minWidth) {
        result.errors.push(`Image too narrow: ${width}px (min: ${requirements.minWidth}px)`);
        result.isValid = false;
      }
      if (width > requirements.maxWidth) {
        result.warnings.push(`Image will be resized: ${width}px exceeds ${requirements.maxWidth}px max`);
      }
      if (height < requirements.minHeight) {
        result.errors.push(`Image too short: ${height}px (min: ${requirements.minHeight}px)`);
        result.isValid = false;
      }
      if (height > requirements.maxHeight) {
        result.warnings.push(`Image will be resized: ${height}px exceeds ${requirements.maxHeight}px max`);
      }

      if (platform === 'instagram') {
        const matchingRatio = requirements.aspectRatios.find(r => 
          Math.abs(aspectRatio - r.ratio) <= r.tolerance
        );
        
        if (matchingRatio) {
          result.aspectRatio = matchingRatio.name;
        } else {
          result.warnings.push(
            `Aspect ratio ${aspectRatio.toFixed(2)}:1 may be cropped. Best: Square (1:1), Portrait (4:5), or Landscape (1.91:1)`
          );
        }
      }

      if (fileSize) {
        const fileSizeMB = fileSize / (1024 * 1024);
        if (fileSizeMB > requirements.maxFileSizeMB) {
          result.errors.push(`File too large: ${fileSizeMB.toFixed(1)}MB (max: ${requirements.maxFileSizeMB}MB)`);
          result.isValid = false;
        }
      }

      if (fileType && !requirements.formats.includes(fileType)) {
        result.warnings.push(`Format ${fileType} may not be optimal. Recommended: JPEG or PNG`);
      }

      newResults[platform] = result;
    }

    return newResults;
  }, [platforms]);

  useEffect(() => {
    if (!file && !url) {
      setResults({});
      return;
    }

    const validate = async () => {
      setIsValidating(true);
      
      try {
        const img = new Image();
        
        const loadPromise = new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject(new Error('Failed to load image'));
        });

        if (file) {
          img.src = URL.createObjectURL(file);
        } else if (url) {
          img.src = url;
        }

        await loadPromise;
        
        const validationResults = await validateImage(
          img, 
          file?.size, 
          file?.type
        );
        
        setResults(validationResults);
        onValidationComplete?.(validationResults);

        if (file) {
          URL.revokeObjectURL(img.src);
        }
      } catch (error) {
        console.error('Validation error:', error);
      } finally {
        setIsValidating(false);
      }
    };

    validate();
  }, [file, url, validateImage, onValidationComplete]);

  if (platforms.length === 0 || (!file && !url)) {
    return null;
  }

  if (isValidating) {
    return (
      <Alert className="bg-muted/50">
        <Info className="h-4 w-4 animate-pulse" />
        <AlertDescription className="text-xs">Validating image...</AlertDescription>
      </Alert>
    );
  }

  const allValid = Object.values(results).every(r => r.isValid);
  const hasWarnings = Object.values(results).some(r => r.warnings.length > 0);
  const hasErrors = Object.values(results).some(r => !r.isValid);

  if (Object.keys(results).length === 0) {
    return null;
  }

  return (
    <div className="space-y-2 mt-2">
      <div className="flex items-center gap-2 text-xs font-medium">
        <ImageIcon className="h-3.5 w-3.5" />
        Image Validation
        {allValid && !hasWarnings && (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs py-0">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Ready
          </Badge>
        )}
        {allValid && hasWarnings && (
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs py-0">
            <AlertTriangle className="h-3 w-3 mr-1" />
            Warnings
          </Badge>
        )}
        {hasErrors && (
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs py-0">
            <XCircle className="h-3 w-3 mr-1" />
            Issues
          </Badge>
        )}
      </div>

      {Object.entries(results).map(([platform, result]) => (
        <div key={platform} className="text-xs space-y-1">
          <div className="flex items-center gap-2">
            <span className="capitalize font-medium">{platform}</span>
            {result.dimensions && (
              <span className="text-muted-foreground">
                {result.dimensions.width} x {result.dimensions.height}px
              </span>
            )}
            {result.aspectRatio && (
              <Badge variant="secondary" className="text-xs py-0">
                {result.aspectRatio}
              </Badge>
            )}
          </div>
          
          {result.errors.map((error, idx) => (
            <Alert key={`error-${idx}`} variant="destructive" className="py-1.5">
              <XCircle className="h-3 w-3" />
              <AlertDescription className="text-xs">{error}</AlertDescription>
            </Alert>
          ))}
          
          {result.warnings.map((warning, idx) => (
            <Alert key={`warning-${idx}`} className="py-1.5 bg-amber-50 border-amber-200">
              <AlertTriangle className="h-3 w-3 text-amber-600" />
              <AlertDescription className="text-xs text-amber-700">{warning}</AlertDescription>
            </Alert>
          ))}
        </div>
      ))}
    </div>
  );
}

export function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
}
